import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getHistorial } from "./api"

const formatFecha = iso => {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const formatNumber = n => (typeof n === "number" ? n.toLocaleString("es-CO") : n ?? "—")

const formatDensity = d => {
  if (d === null || d === undefined) return "—"
  const num = typeof d === "string" ? parseFloat(d) : d
  return isNaN(num) ? "—" : `${num.toFixed(2)}%`
}

const TabHistorial = ({ nitemisor, prefijo }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError("")
    getHistorial({ nitemisor, prefijo, limit: 50 })
      .then(({ data }) => {
        if (cancelled) return
        const lista = Array.isArray(data) ? data : data?.snapshots || data?.historial || []
        setItems(lista)
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.message || "No se pudo cargar el historial.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nitemisor, prefijo])

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <Spinner color="primary" />
        <div className="mt-2">Cargando historial…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#dc2626", background: "#fef2f2", borderRadius: 10 }}>
        <i className="mdi mdi-alert-circle me-2" />
        {error}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          background: "#f8fafc",
          borderRadius: 12,
          border: "1px dashed #cbd5e1",
        }}
      >
        <i className="mdi mdi-clock-outline" style={{ fontSize: 48, color: "#94a3b8" }} />
        <h6 className="mt-3" style={{ color: "#0f172a" }}>Sin snapshots</h6>
        <p style={{ color: "#64748b", margin: 0 }}>
          Aún no hay análisis previos guardados para este prefijo.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ color: "#0f172a", fontWeight: 600 }}>
          {items.length} snapshot{items.length !== 1 ? "s" : ""}
        </h6>
        <span style={{ fontSize: 12, color: "#64748b" }}>Más reciente arriba</span>
      </div>

      <div style={{ position: "relative", paddingLeft: 28 }}>
        <div
          style={{
            position: "absolute",
            left: 9,
            top: 8,
            bottom: 8,
            width: 2,
            background: "#e2e8f0",
          }}
        />
        {items.map((snap, i) => {
          const fecha = snap.fecha || snap.timestamp || snap.created_at
          const gaps = snap.gaps_estimados ?? snap.gaps ?? snap.total_gaps
          const folioMax = snap.folio_max ?? snap.folio_maximo
          const densidad = snap.densidad ?? snap.densidad_general
          const totalFolios = snap.total_folios

          return (
            <div key={i} style={{ position: "relative", paddingBottom: 20 }}>
              <div
                style={{
                  position: "absolute",
                  left: -22,
                  top: 6,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: i === 0 ? "#3b82f6" : "#cbd5e1",
                  border: "3px solid #fff",
                  boxShadow: "0 0 0 1px #cbd5e1",
                }}
              />
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: "12px 16px",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                    {formatFecha(fecha)}
                  </span>
                  {snap.prefijo && (
                    <span
                      style={{
                        padding: "2px 8px",
                        background: "#f1f5f9",
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      {snap.prefijo}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gap: 8,
                    fontSize: 12,
                  }}
                >
                  {gaps !== undefined && (
                    <div>
                      <div style={{ color: "#94a3b8" }}>Gaps</div>
                      <div style={{ color: "#dc2626", fontWeight: 700, fontSize: 15 }}>
                        {formatNumber(gaps)}
                      </div>
                    </div>
                  )}
                  {totalFolios !== undefined && (
                    <div>
                      <div style={{ color: "#94a3b8" }}>Folios</div>
                      <div style={{ color: "#0f172a", fontWeight: 600, fontSize: 15 }}>
                        {formatNumber(totalFolios)}
                      </div>
                    </div>
                  )}
                  {folioMax !== undefined && (
                    <div>
                      <div style={{ color: "#94a3b8" }}>Folio máx.</div>
                      <div style={{ color: "#0f172a", fontWeight: 600, fontSize: 15 }}>
                        {formatNumber(folioMax)}
                      </div>
                    </div>
                  )}
                  {densidad !== undefined && (
                    <div>
                      <div style={{ color: "#94a3b8" }}>Densidad</div>
                      <div style={{ color: "#0f172a", fontWeight: 600, fontSize: 15 }}>
                        {formatDensity(densidad)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

TabHistorial.propTypes = {
  nitemisor: PropTypes.string.isRequired,
  prefijo: PropTypes.string,
}

export default TabHistorial
